/**
 * The data panel's **Bar Intel** tab — Bar Intelligence™ as tables.
 *
 * `docs/plans/bar-intelligence.md`. The chart computes the numbers against its
 * own bars and publishes them (`lib/bar-intel-store.ts`); this renders what was
 * published for the chart the panel was opened on. Nothing is fetched and
 * nothing is computed here, so the panel and the labels on the chart can never
 * disagree.
 *
 * Settings live in the indicator's own dialog (Q13), so the header only says
 * what they are.
 */

import { useState, type CSSProperties, type ReactElement } from "react";
import { useBarIntel } from "../lib/bar-intel-store";
import { runAction } from "../lib/action-runner";
import { BarIntelRanges } from "./BarIntelRanges";
import {
  fmtPrice,
  fmtSigned,
  periodLabel,
  type BiLevel,
  type BiPeriod,
} from "../lib/bar-intelligence";
import type { PatternRow } from "../lib/bar-intelligence-patterns";
import { R_LEVELS, type SetupStats } from "../lib/bar-setups";

const ORDER: readonly BiPeriod[] = [
  "d",
  "w",
  "m",
  "pq",
  "mtd",
  "q",
  "y",
  "52w",
  "ath",
];

const date = (t: number) => new Date(t * 1000).toISOString().slice(0, 10);

/**
 * Visual or Table — Visual by default (owner, 2026-10-12). A panel preference,
 * so `localStorage`, per browser; storage that throws reads as the default.
 */
type LevelsView = "visual" | "table";
const VIEW_KEY = "qf.barIntel.view";
function loadView(): LevelsView {
  try {
    return localStorage.getItem(VIEW_KEY) === "table" ? "table" : "visual";
  } catch {
    return "visual";
  }
}
function saveView(view: LevelsView): void {
  try {
    localStorage.setItem(VIEW_KEY, view);
  } catch {
    // A private window: the choice lasts this session only.
  }
}

export function BarIntelView({
  chartGroupId,
}: {
  symbol: string;
  chartGroupId?: string;
}): ReactElement {
  const snap = useBarIntel(chartGroupId);
  const [view, setView] = useState<LevelsView>(loadView);
  const pick = (v: LevelsView) => {
    setView(v);
    saveView(v);
  };
  if (!snap) {
    return <AddBarIntel chartGroupId={chartGroupId} />;
  }
  const { params, result, patterns, unit } = snap;
  // The live level per period and side; a crossed one still showing is listed under it.
  const live = new Map<string, BiLevel>();
  const ghosts: BiLevel[] = [];
  for (const level of result.levels) {
    if (level.key === `${level.period}:${level.side}`)
      live.set(level.key, level);
    else ghosts.push(level);
  }
  const periods = ORDER.filter(
    (p) => live.has(`${p}:high`) || live.has(`${p}:low`),
  );

  return (
    <div style={ROOT}>
      <div style={HEAD}>
        <span style={TITLE}>Bar Intelligence™ · {snap.symbol}</span>
        <span style={MUTED}>
          near ≤ {params.near}% · crossed ≥ {params.cross}% · hide after{" "}
          {params.hideDays}d · from{" "}
          {params.measure === "high" ? "High" : "Close"}
        </span>
        <span style={SWITCH} role="group" aria-label="Levels view">
          {(["visual", "table"] as const).map((v) => (
            <button
              key={v}
              type="button"
              aria-pressed={view === v}
              style={view === v ? SEG_ON : SEG}
              onClick={() => pick(v)}
            >
              {v === "visual" ? "Visual" : "Table"}
            </button>
          ))}
        </span>
      </div>

      {view === "visual" ? (
        <>
          <div style={MUTED}>
            close {fmtPrice(snap.current.c)} · {date(snap.current.t)} · distance
            = (close − level) ÷ level
          </div>
          <BarIntelRanges
            live={live}
            close={snap.current.c}
            near={params.near}
          />
        </>
      ) : (
        <table style={TABLE}>
          <thead>
            <tr>
              <th style={TH} />
              <th style={{ ...TH_R, ...DIVIDE }}>high</th>
              <th style={TH_R}>set</th>
              <th style={TH_R}>dur</th>
              <th style={TH_R}>distance</th>
              <th
                style={TH_R}
                title="YTD and 52W: retests since the level was set, within Near %"
              >
                touch
              </th>
              <th
                style={{ ...TH_R, ...DIVIDE }}
                title="Width of the period's range: (high − low) ÷ low"
              >
                band
              </th>
              <th style={{ ...TH_R, ...DIVIDE }}>low</th>
              <th style={TH_R}>set</th>
              <th style={TH_R}>dur</th>
              <th style={TH_R}>distance</th>
              <th
                style={TH_R}
                title="YTD and 52W: retests since the level was set, within Near %"
              >
                touch
              </th>
            </tr>
          </thead>
          <tbody>
            {/* The current bar — what every distance is measured from (owner, 2026-10-12). */}
            <tr style={CURRENT}>
              <td style={TD}>Current {date(snap.current.t).slice(5)}</td>
              <td style={{ ...TD_R, ...DIVIDE }}>{fmtPrice(snap.current.h)}</td>
              <td style={{ ...TD_R, ...MUTED }} colSpan={4}>
                close {fmtPrice(snap.current.c)}
              </td>
              <td style={{ ...TD_R, ...DIVIDE }}>
                {snap.current.l > 0
                  ? `${(((snap.current.h - snap.current.l) / snap.current.l) * 100).toFixed(1)}%`
                  : "—"}
              </td>
              <td style={{ ...TD_R, ...DIVIDE }}>{fmtPrice(snap.current.l)}</td>
              <td style={TD_R} colSpan={4} />
            </tr>
            {periods.map((p) => (
              <tr key={p}>
                <td style={TD}>{periodLabel(p)}</td>
                <LevelCells level={live.get(`${p}:high`)} />
                <td style={{ ...TD_R, ...DIVIDE }}>
                  {band(live.get(`${p}:high`), live.get(`${p}:low`))}
                </td>
                <LevelCells level={live.get(`${p}:low`)} />
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {view === "table" && result.rangePos.length > 0 && (
        <div style={NOTE}>
          range position{" "}
          {result.rangePos
            .map((r) => `${periodLabel(r.period)} ${Math.round(r.pct)}%`)
            .join(" · ")}
        </div>
      )}

      {/* Crossed on the left, new today on the right; stacked when narrow. */}
      {(ghosts.length > 0 || result.newToday.length > 0) && (
        <div style={SIDE}>
          {ghosts.length > 0 && (
            <div style={SIDE_COL}>
              {ghosts.map((g) => (
                <div key={g.key}>
                  ✕ {g.name} {fmtPrice(g.price)} crossed {g.crossed?.daysAgo}d
                  ago — goes in {g.crossed?.left}d
                </div>
              ))}
            </div>
          )}
          {result.newToday.length > 0 && (
            <div style={SIDE_COL}>
              {result.newToday.map((n) => (
                <div key={`${n.period}:${n.side}`} style={STAR}>
                  ★ new {periodLabel(n.period)} {n.side} today
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <div style={{ ...TITLE, marginTop: 14 }}>Patterns</div>
      {patterns.length === 0 ? (
        <div style={MUTED}>
          No pattern on this chart. Patterns appear here when a pattern plot
          finds one.
        </div>
      ) : (
        <table style={TABLE}>
          <thead>
            <tr>
              <th style={TH}>pattern</th>
              <th style={TH_R}>start</th>
              <th style={TH}>status</th>
              <th style={TH_R}>depth</th>
              <th style={TH_R}>dur</th>
              <th style={TH_R}>trigger</th>
              <th style={TH_R}>to trig</th>
              <th style={TH_R}>target</th>
              <th style={TH_R}>to tgt</th>
              <th style={TH_R}>stop</th>
              <th style={TH_R}>R:R</th>
            </tr>
          </thead>
          <tbody>
            {patterns.map((row) => (
              <PatternRows key={row.id} row={row} unit={unit} />
            ))}
          </tbody>
        </table>
      )}
      <SetupSection
        title="Inside bar setup"
        rule={`buy stop high + $${params.entry.toFixed(2)} · stop the inside bar's low`}
        stats={snap.insideSetup}
      />
      <SetupSection
        title="Outside bar setup"
        rule="next bar closes above the high, bought at that close · stop the outside bar's low"
        stats={snap.outsideSetup}
      />
    </div>
  );
}

const pct = (part: number, whole: number) => (whole > 0 ? `${Math.round((part / whole) * 100)}%` : "");

/**
 * One of the owner's bar setups over the chart's bars (`lib/bar-setups.ts`):
 * how many fired, how many triggered, and how far each got before the stop.
 * No target was given, so 1R/2R/3R are counted rather than one "worked".
 */
function SetupSection({ title, rule, stats }: { title: string; rule: string; stats: SetupStats }): ReactElement {
  const last = stats.last;
  const lastText = !last
    ? null
    : !last.triggered
      ? "not triggered"
      : last.stopped
        ? `stopped, best ${fmtSigned(last.maxR ?? 0, 1)}R`
        : `open, best ${fmtSigned(last.maxR ?? 0, 1)}R`;
  return (
    <>
      <div style={{ ...TITLE, marginTop: 14 }}>
        {title}{" "}
        <span style={{ ...MUTED, fontWeight: 400 }}>
          · {rule} · {stats.bars.toLocaleString()} bars
        </span>
      </div>
      {stats.setups === 0 ? (
        <div style={MUTED}>No setup on this chart's bars.</div>
      ) : (
        <>
          <table style={TABLE}>
            <thead>
              <tr>
                <th style={TH_R}>setups</th>
                <th style={TH_R}>triggered</th>
                <th style={TH_R}>stopped</th>
                {R_LEVELS.map((r) => (
                  <th key={r} style={TH_R}>
                    reached {r}R
                  </th>
                ))}
                <th style={TH_R}>still open</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style={TD_R}>{stats.setups}</td>
                <td style={TD_R}>{stats.triggered}</td>
                <td style={TD_R}>{stats.stopped}</td>
                {stats.reached.map((n, at) => (
                  <td key={at} style={TD_R}>
                    {n} <span style={MUTED}>{pct(n, stats.triggered)}</span>
                  </td>
                ))}
                <td style={TD_R}>{stats.open}</td>
              </tr>
            </tbody>
          </table>
          <div style={NOTE}>
            {stats.avgMaxR !== undefined && (
              <>
                avg best before stop <b>{stats.avgMaxR.toFixed(1)}R</b> ·{" "}
              </>
            )}
            {last && (
              <>
                last setup {date(last.t)} · {lastText}
              </>
            )}
          </div>
        </>
      )}
    </>
  );
}

/** How wide the period's range is: (high − low) ÷ low. */
function band(high: BiLevel | undefined, low: BiLevel | undefined): string {
  if (!high || !low || low.price <= 0) return "—";
  const pct = ((high.price - low.price) / low.price) * 100;
  return `${pct < 100 ? pct.toFixed(1) : Math.round(pct)}%`;
}

function LevelCells({ level }: { level: BiLevel | undefined }): ReactElement {
  if (!level) {
    return (
      <>
        <td style={{ ...TD_R, ...DIVIDE }}>—</td>
        <td style={TD_R} />
        <td style={TD_R} />
        <td style={TD_R} />
        <td style={TD_R} />
      </>
    );
  }
  const mark =
    level.state === "crossed"
      ? " ✕"
      : level.state === "near"
        ? " ◆"
        : level.state === "through"
          ? level.side === "high"
            ? " ▲"
            : " ▼"
          : "";
  return (
    <>
      <td style={{ ...TD_R, ...DIVIDE }}>{fmtPrice(level.price)}</td>
      <td style={{ ...TD_R, ...MUTED }}>{date(level.setT)}</td>
      <td style={TD_R}>{level.days}d</td>
      <td
        style={{
          ...TD_R,
          ...(level.state === "near" || level.state === "through" ? WARN : {}),
        }}
      >
        {fmtSigned(level.distancePct, 1)}%{mark}
      </td>
      <td style={TD_R}>{level.touches ?? ""}</td>
    </>
  );
}

function PatternRows({
  row,
  unit,
}: {
  row: PatternRow;
  unit: string;
}): ReactElement {
  const num = (v: number | undefined) => (v === undefined ? "—" : fmtPrice(v));
  const pc = (v: number | undefined) =>
    v === undefined ? "—" : `${fmtSigned(v, 1)}%`;
  const status =
    row.sinceBreakout && row.statusText !== "target ✓"
      ? `${row.status} ${row.sinceBreakout.bars}${unit}`
      : row.statusText;
  return (
    <>
      <tr>
        <td style={TD}>{row.name}</td>
        <td style={{ ...TD_R, ...MUTED }}>
          {row.start === undefined ? "—" : date(row.start)}
        </td>
        <td style={TD}>{status}</td>
        <td style={TD_R}>
          {row.depthPct === undefined ? "—" : `${row.depthPct.toFixed(1)}%`}
        </td>
        <td style={TD_R}>
          {row.bars === undefined ? "—" : `${row.bars}${unit}`}
        </td>
        <td style={TD_R}>{num(row.trigger)}</td>
        <td style={TD_R}>{pc(row.toTriggerPct)}</td>
        <td style={TD_R}>{num(row.target)}</td>
        <td style={TD_R}>
          {row.targetHit?.hit
            ? `hit ${row.targetHit.bars}${unit}`
            : pc(row.toTargetPct)}
        </td>
        <td style={TD_R}>{num(row.stop)}</td>
        <td style={TD_R}>{row.rr === undefined ? "—" : row.rr.toFixed(1)}</td>
      </tr>
      {(row.handle || row.sinceBreakout || row.touches !== undefined) && (
        <tr>
          <td style={{ ...TD, ...MUTED, paddingLeft: 14 }} colSpan={11}>
            {[
              row.handle &&
                `handle ${row.handle.depthPct === undefined ? "" : `${row.handle.depthPct.toFixed(1)}% · `}${row.handle.bars}${unit}`,
              row.sinceBreakout &&
                `since breakout ${row.sinceBreakout.bars}${unit} · ${fmtSigned(row.sinceBreakout.pct, 1)}%`,
              row.touches !== undefined && `${row.touches} touches`,
            ]
              .filter(Boolean)
              .join("  ·  ")}
          </td>
        </tr>
      )}
    </>
  );
}

const ROOT: CSSProperties = {
  padding: "10px 12px",
  fontSize: 12,
  overflow: "auto",
  height: "100%",
};
const HEAD: CSSProperties = {
  display: "flex",
  flexWrap: "wrap",
  gap: 10,
  alignItems: "baseline",
  marginBottom: 8,
};
const TITLE: CSSProperties = { fontWeight: 600, fontSize: 13 };
const MUTED: CSSProperties = { color: "var(--qf-text-secondary)" };
const WARN: CSSProperties = { color: "var(--qf-warn)" };
const STAR: CSSProperties = { color: "var(--qf-warn)" };
const TABLE: CSSProperties = {
  borderCollapse: "collapse",
  width: "100%",
  fontVariantNumeric: "tabular-nums",
};
const TH: CSSProperties = {
  textAlign: "left",
  fontWeight: 500,
  color: "var(--qf-text-secondary)",
  padding: "3px 6px",
  borderBottom: "1px solid var(--qf-border)",
  whiteSpace: "nowrap",
};
const TH_R: CSSProperties = { ...TH, textAlign: "right" };
const TD: CSSProperties = { padding: "3px 6px", whiteSpace: "nowrap" };
const TD_R: CSSProperties = { ...TD, textAlign: "right" };
/** The rule that sets the high group, the band and the low group apart. */
const DIVIDE: CSSProperties = { borderLeft: "1px solid var(--qf-border)" };
const SWITCH: CSSProperties = {
  marginLeft: "auto",
  display: "inline-flex",
  border: "1px solid var(--qf-border)",
  borderRadius: 6,
  overflow: "hidden",
};
const SEG: CSSProperties = {
  font: "inherit",
  fontSize: 12,
  padding: "2px 10px",
  border: "none",
  background: "transparent",
  color: "var(--qf-text-secondary)",
  cursor: "pointer",
};
const SEG_ON: CSSProperties = {
  ...SEG,
  background: "var(--qf-accent)",
  color: "var(--qf-surface)",
};
const SIDE: CSSProperties = {
  display: "flex",
  flexWrap: "wrap",
  columnGap: 32,
  rowGap: 8,
  marginTop: 8,
  lineHeight: 1.6,
};
const SIDE_COL: CSSProperties = { flex: "1 1 260px", minWidth: 0 };
/** The current bar's row: the reference line under the header, set apart from the levels. */
const CURRENT: CSSProperties = {
  fontWeight: 600,
  color: "var(--qf-accent)",
  borderBottom: "1px solid var(--qf-border)",
};
const NOTE: CSSProperties = { marginTop: 8, lineHeight: 1.6 };
const EMPTY: CSSProperties = {
  padding: 16,
  color: "var(--qf-text-secondary)",
  lineHeight: 1.5,
};

/**
 * The empty state is the way in: **Add Bar Intelligence** is a link that puts
 * the indicator on this panel's chart (owner, 2026-10-13), through the same
 * `addIndicator` action the agent calls. A refusal — a plan without it, a
 * catalog not loaded yet — is shown under the link in the action's own words.
 */
function AddBarIntel({ chartGroupId }: { chartGroupId?: string }): ReactElement {
  const [refused, setRefused] = useState<string | null>(null);
  const add = () => {
    const outcome = runAction("addIndicator", {
      indicator: "barIntel",
      ...(chartGroupId ? { chartGroupId } : {}),
    });
    setRefused(outcome.ok ? null : outcome.reason);
  };
  return (
    <div style={EMPTY}>
      <button type="button" onClick={add} style={LINK}>
        Add Bar Intelligence
      </button>{" "}
      to this chart to see its period highs and lows, and the stats of any
      pattern on it.
      {refused && <div style={REFUSED}>Could not add it: {refused}.</div>}
    </div>
  );
}

const LINK: CSSProperties = {
  background: "none",
  border: "none",
  padding: 0,
  font: "inherit",
  fontWeight: 600,
  color: "var(--qf-accent)",
  textDecoration: "underline",
  cursor: "pointer",
};

const REFUSED: CSSProperties = { marginTop: 8, color: "var(--qf-negative)" };
